// src/hooks/useDocumentTypes.ts

import { useState, useEffect, useCallback } from "react";
import {
  DocumentTypeCreateRequest,
  DocumentTypeUpdateRequest,
  DocumentTypeResponse as DocumentType,
} from "../types/documentTypes";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { EligibleWorkspace } from "../types/eligibleWorkspace";
import { CopyDocumentTypeRequest } from "../types/copyDocumentTypeRequest";
import { ValidateCopyNameRequest } from "../types/validateCopyNameRequest";
import { ValidateCopyNameResponse } from "../types/validateCopyNameResponse";

export const useFetchDocumentTypes = (workspace_id: string) => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // const { user } = useAuth();
  const authFetch = useAuthFetch();

  const refresh = () => setRefreshTrigger((prev) => prev + 1);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const response = await authFetch(
          `/api/workspace/meridian/admin/document_types?workspace_id=${workspace_id}`,
          "fetch document types",
        );
        if (Array.isArray(response)) {
          setDocumentTypes(response);
        } else {
          console.error("Unexpected response format:", response);
          setError("Received invalid data format from server");
        }
      } catch (err: any) {
        console.error("Error fetching document types:", err);
        setError(err.message || "Error fetching document types.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentTypes();
  }, [refreshTrigger, workspace_id, authFetch]);

  return { documentTypes, loading, error, refresh };
};

export const useFetchDocumentType = (
  documentTypeId: number,
  workspace_id: string,
  documentTypeVersion?: number,
  status: "Draft" | "Published" | "Latest" = "Published",
) => {
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    const fetchDocumentType = async () => {
      if (!documentTypeId || !workspace_id) {
        setDocumentType(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        var url = `/api/workspace/meridian/admin/document_types/${documentTypeId}?workspace_id=${workspace_id}`;

        if (status !== "Latest") {
          url += `&status_filter=${status}`;
        }

        if (documentTypeVersion) {
          url += `&version=${documentTypeVersion}`;
        }

        const response = await authFetch(url, "fetch document type");
        setDocumentType(response);
      } catch (err: any) {
        console.error("Error fetching document type:", err);
        setError(err.message || "Error fetching document type configuration.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentType();
  }, [documentTypeId, workspace_id, status, authFetch, documentTypeVersion]);

  return { documentType, loading, error };
};

export const useFetchDocumentTypeVersions = (
  documentTypeId: number,
  workspace_id: string,
) => {
  const [versions, setVersions] = useState<DocumentType[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    const fetchVersions = async () => {
      if (!documentTypeId || !workspace_id) {
        setVersions(null);
        setError(null);
        return;
      }
      try {
        setLoading(true);
        setVersions(null);
        setError(null);
        const response = await authFetch(
          `/api/workspace/meridian/admin/document_types/${documentTypeId}/versions?workspace_id=${workspace_id}`,
          "fetch document type versions",
        );
        setVersions(Array.isArray(response) ? response : []);
      } catch (err: any) {
        setError(err.message || "Error fetching document type versions.");
      } finally {
        setLoading(false);
      }
    };
    fetchVersions();
  }, [documentTypeId, workspace_id, authFetch]);

  return { versions, loading, error };
};

export const useFetchDocumentTypeForDocument = (
  documentTypeId: number,
  workspace_id: string,
  documentTypeVersion?: number,
) => {
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    const fetchDocumentTypeForDocument = async () => {
      if (!documentTypeId || !workspace_id) {
        setDocumentType(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        var url = `/api/workspace/meridian/document_types/${documentTypeId}?workspace_id=${workspace_id}`;
        if (documentTypeVersion) {
          url += `&version=${documentTypeVersion}`;
        }

        const response = await authFetch(
          url,
          "fetch document type for document",
        );
        setDocumentType(response);
      } catch (err: any) {
        console.error("Error fetching document type for document:", err);
        setError(err.message || "Error fetching document type configuration.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentTypeForDocument();
  }, [documentTypeId, workspace_id, documentTypeVersion, authFetch]);

  return { documentType, loading, error };
};

export const useDocumentTypes = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const createDocumentType = async (
    data: DocumentTypeCreateRequest,
  ): Promise<DocumentType> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types?workspace_id=${workspace_id}`,
        "create document type",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      return response;
    } catch (err: any) {
      setError(err.message || "Error creating document type.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateDocumentType = async (
    documentTypeId: number,
    updates: DocumentTypeUpdateRequest,
  ): Promise<void> => {
    try {
      setLoading(true);
      const requestBody = {
        Name: updates.Name,
        Description: updates.Description || "",
        CustomInstructions: updates.CustomInstructions || "",
        Status: updates.Status || "Draft",
      };
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}?workspace_id=${workspace_id}`,
        "update document type",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );
    } catch (err: any) {
      setError(err.message || "Error updating document type.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createNewDocumentTypeVersion = async (
    documentTypeId: number,
  ): Promise<DocumentType> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}?workspace_id=${workspace_id}`,
        "create new document type version",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response?.documentType) {
        throw new Error("No document type returned from server");
      }

      return response.documentType;
    } catch (err: any) {
      const errorMessage = err.message || "Error creating new version.";
      setError(errorMessage);
      console.error("Failed to create new version:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const rollbackDocumentTypeVersion = async (
    documentTypeId: number,
    targetVersion: number,
  ): Promise<DocumentType> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/rollback?workspace_id=${workspace_id}`,
        "roll back document type version",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target_version: targetVersion }),
        },
      );
      if (!response?.documentType) {
        throw new Error("No document type returned from server");
      }
      return response.documentType;
    } catch (err: any) {
      const errorMessage = err.message || "Error rolling back document type.";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    createDocumentType,
    updateDocumentType,
    createNewDocumentTypeVersion,
    rollbackDocumentTypeVersion,
    loading,
    error,
  };
};

export const useDeleteDocumentType = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const deleteDocumentType = async (documentTypeId: number) => {
    try {
      setLoading(true);
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}?workspace_id=${workspace_id}`,
        "delete document type",
        {
          method: "DELETE",
        },
      );
    } catch (err: any) {
      setError(err.message || "Error deleting document type.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteDocumentType, loading, error };
};

export const usePublishDocumentType = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const publishDocumentType = async (documentTypeId: number): Promise<void> => {
    try {
      setLoading(true);
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/publish?workspace_id=${workspace_id}`,
        "publish document type",
        {
          method: "POST",
        },
      );
    } catch (err: any) {
      setError(err.message || "Error publishing document type.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { publishDocumentType, loading, error };
};

export const useCreateNewDocumentTypeVersion = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const createNewDocumentTypeVersion = async (
    documentTypeId: number,
  ): Promise<DocumentType> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}?workspace_id=${workspace_id}`,
        "create new document type version",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response?.documentType) {
        throw new Error("No document type returned from server");
      }

      return response.documentType;
    } catch (err: any) {
      const errorMessage = err.message || "Error creating new version.";
      setError(errorMessage);
      console.error("Failed to create new version:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  return { createNewDocumentTypeVersion, loading, error, clearError };
};

export const useFetchPublishedDocumentTypes = (workspace_id: string) => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const authFetch = useAuthFetch();

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/published?workspace_id=${workspace_id}`,
        "fetch Meridian One document types.",
      );
      if (Array.isArray(response)) {
        setDocumentTypes(response);
      } else {
        console.error("Unexpected response format:", response);
        setError("Received invalid data format from server");
      }
    } catch (err: any) {
      console.error("Error fetching document types:", err);
      setError(err.message || "Error fetching document types.");
    } finally {
      setLoading(false);
    }
  }, [workspace_id, authFetch]);

  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchDocumentTypes();
  }, [workspace_id, refreshTrigger, fetchDocumentTypes]);

  return { documentTypes, loading, error, refresh };
};

export const useEligibleWorkspacesForCopy = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const getEligibleWorkspacesForCopy = useCallback(
    async (excludeWorkspaceId: string): Promise<EligibleWorkspace[]> => {
      try {
        setLoading(true);
        setError(null);

        const url = `/api/workspace/meridian/admin/eligible_workspaces_for_copy?exclude_workspace_id=${encodeURIComponent(excludeWorkspaceId)}`;

        const response = await authFetch(
          url,
          "fetch eligible workspaces for copy",
        );
        return Array.isArray(response) ? response : [];
      } catch (err: any) {
        const errorMessage =
          err.message || "Error fetching eligible workspaces.";
        setError(errorMessage);
        throw err; // Re-throw to allow caller to handle HTTP status codes
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  return { getEligibleWorkspacesForCopy, loading, error };
};

export const useCopyDocumentType = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{
    status?: number;
    message: string;
  } | null>(null);
  const authFetch = useAuthFetch();

  const copyDocumentType = async (
    documentTypeId: number,
    request: CopyDocumentTypeRequest,
    workspaceId: string,
  ): Promise<DocumentType> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/copy?workspace_id=${encodeURIComponent(workspaceId)}`,
        "copy document type",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        },
      );

      return response.documentType || response;
    } catch (err: any) {
      // Handle different HTTP error codes
      let errorMessage =
        "An error occurred while copying the document type. Please try again.";
      let statusCode: number | undefined;

      if (err.status === 400) {
        statusCode = 400;
        const detail = err.response?.detail || err.detail || "Invalid request.";
        if (detail.includes("Invalid workspace")) {
          errorMessage = "Invalid workspace selected.";
        } else {
          errorMessage = detail;
        }
      } else if (err.status === 403) {
        statusCode = 403;
        const detail = err.response?.detail || err.detail || "";
        if (detail.includes("source workspace")) {
          errorMessage =
            "You do not have Editor or Owner access to the source workspace.";
        } else {
          errorMessage =
            "You do not have Editor or Owner access to the target workspace.";
        }
      } else if (err.status === 404) {
        statusCode = 404;
        const detail =
          err.response?.detail || err.detail || "Resource not found.";
        if (detail.includes("Document type")) {
          errorMessage = "Document type not found.";
        } else if (detail.includes("workspace")) {
          errorMessage = "Target workspace not found.";
        } else {
          errorMessage = detail;
        }
      } else if (err.status === 500) {
        statusCode = 500;
        errorMessage =
          "An error occurred while copying the document type. Please try again.";
      } else {
        errorMessage = err.message || "Error copying document type.";
      }

      const errorObj = { status: statusCode, message: errorMessage };
      setError(errorObj);
      const error = new Error(errorMessage);
      (error as any).status = statusCode;
      (error as any).errorObj = errorObj;
      throw error; // Re-throw with error object for dialog handling
    } finally {
      setLoading(false);
    }
  };

  return { copyDocumentType, loading, error };
};

export const useValidateCopyName = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const validateCopyName = async (
    documentTypeId: number,
    workspaceId: string,
    targetWorkspaceId: string,
    newName?: string,
  ): Promise<ValidateCopyNameResponse> => {
    setLoading(true);
    setError(null);

    try {
      const requestBody: ValidateCopyNameRequest = {
        target_workspace_id: targetWorkspaceId,
        new_name: newName,
      };

      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/copy/validate-name?workspace_id=${encodeURIComponent(workspaceId)}`,
        "validate copy name",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      return response as ValidateCopyNameResponse;
    } catch (err: any) {
      const errorMessage =
        err.message || "Error validating document type name.";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { validateCopyName, loading, error };
};
